import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import * as Dialog from '@radix-ui/react-dialog'

/**
 * A change to an order that has to say why: cancel, restore, take a line off.
 * After tperent's `CancelOrderDialog` and `RestoreOrderDialog`, which are the
 * same form twice — a question, what it will do, a required reason, and the
 * reason's promise that it stays on the order's history.
 *
 * Centered and small (`st-dialog`) rather than a sheet: it is one question,
 * asked over the page it is about.
 */
export function ReasonDialog({
  open,
  title,
  children,
  placeholder,
  confirmLabel,
  pendingLabel,
  tone = 'default',
  pending,
  error,
  footnote = 'It stays on the order’s history, with your name and the time.',
  onConfirm,
  onClose,
}: {
  readonly open: boolean
  readonly title: string
  /** What pressing the button will do, said before it is pressed. */
  readonly children?: ReactNode
  readonly placeholder: string
  readonly confirmLabel: string
  readonly pendingLabel: string
  readonly tone?: 'default' | 'alert'
  readonly pending: boolean
  readonly error: string | undefined
  /** Where the reason is kept, said under the field. */
  readonly footnote?: string
  readonly onConfirm: (reason: string) => void
  readonly onClose: () => void
}) {
  const [reason, setReason] = useState('')
  // A fresh question every time it opens: last time's reason is not this one's.
  useEffect(() => {
    if (open) setReason('')
  }, [open])

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (reason.trim()) onConfirm(reason.trim())
  }

  return (
    <Dialog.Root open={open} onOpenChange={(next) => (next ? undefined : onClose())}>
      <Dialog.Portal>
        <Dialog.Overlay className="st-overlay" />
        <Dialog.Content className="st-dialog" aria-describedby={undefined}>
          <form onSubmit={submit}>
            <Dialog.Title className="st-display mb-2 text-lg">{title}</Dialog.Title>
            {children ? <div className="st-meta mb-4 text-sm">{children}</div> : null}
            <label className="flex flex-col gap-1">
              <span className="st-eyebrow">Reason</span>
              <textarea
                className="st-input"
                rows={3}
                required
                autoFocus
                maxLength={500}
                placeholder={placeholder}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </label>
            <p className="st-meta mt-1.5 text-xs">{footnote}</p>
            {error ? (
              <p className="st-alert mt-3" role="alert">
                {error}
              </p>
            ) : null}
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" className="st-btn-ghost" onClick={onClose}>
                Not now
              </button>
              <button
                type="submit"
                className="st-btn"
                style={tone === 'alert' ? { background: '#8d3f2c', borderColor: '#8d3f2c' } : undefined}
                disabled={!reason.trim() || pending}
              >
                {pending ? pendingLabel : confirmLabel}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
